import { FormProvider, useForm } from 'react-hook-form';
import {
    useEditOwnerContractorMutation,
    useGetIndividualOwnerContractorQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import { ModalProps } from '../../../types/modal';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputLabel,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import React, { useEffect } from 'react';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import toast from '../../../shared/Toast';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

interface ContractorModalProps extends ModalProps {
    id?: any;
}

const Schema = z.object({
    permissions: z.enum(['disabled', 'readOnly', 'readAndWrite']),
});

const ContractorModal = ({ id, open, onClose }: ContractorModalProps) => {
    const loggedInUser = UserRole();

    const { data: contractor } = useGetIndividualOwnerContractorQuery(
        [loggedInUser.company, id],
        { skip: !loggedInUser?.company },
    );

    const [updateContractor] = useEditOwnerContractorMutation();

    type FormValues = z.infer<typeof Schema>;

    const methods = useForm<FormValues>({
        defaultValues: {
            permissions: 'disabled',
        },
        resolver: zodResolver(Schema),
    });

    const { control, handleSubmit, reset } = methods;

    const onSubmit = async (data: FormValues) => {
        try {
            const request = {
                contractorId: id,
                ...data,
            };

            await updateContractor([loggedInUser?.company, request]).unwrap();
            onClose();

            toast.success('Contractor updated successfully');
        } catch (error) {
            toast.error('Error updating contractor');
        }
    };

    useEffect(() => {
        if (contractor) reset(contractor);
    }, [contractor]);

    return (
        <Dialog open={open} onClose={onClose}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {contractor && (
                        <>
                            <DialogTitle>
                                {contractor.name}
                                <IconButton
                                    onClick={onClose}
                                    sx={{ color: 'text.primary' }}
                                    aria-label='close'
                                >
                                    <Close />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent sx={{ minWidth: 350 }}>
                                <InputLabel required>Permissions</InputLabel>
                                <FormAutocomplete
                                    name='permissions'
                                    control={control}
                                    options={[
                                        {
                                            value: 'disabled',
                                            label: 'Disabled',
                                        },
                                        {
                                            value: 'readOnly',
                                            label: 'Read Only',
                                        },
                                        {
                                            value: 'readAndWrite',
                                            label: 'Read And Write',
                                        },
                                    ]}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button variant='contained' type='submit'>
                                    Save
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default ContractorModal;
